import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { dashboardApi, documentsApi } from '@services/api';
import { useAuth } from '@hooks';
import {
  formatMoney,
  vatWarningLevel,
  moneyToCents,
  centsToMoney,
  nassauToday,
  NASSAU_TZ,
  VAT_SOFT_THRESHOLD,
  VAT_STRONG_THRESHOLD,
} from '@utils';
import { LoadingSpinner, EmptyState } from '@components/common';
import type { DashboardData, DocumentListItem } from '@types';

function greeting(now: Date = new Date()): string {
  const hour = Number(
    new Intl.DateTimeFormat('en-US', {
      timeZone: NASSAU_TZ,
      hour: 'numeric',
      hour12: false,
    }).format(now)
  );
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Semicircle VAT gauge. `revenue` is the server's trailing-12m figure (F8.2);
 * the arc fills toward the $100k BSD ceiling with the $80k soft tick marked.
 * Compared, never summed — no client money arithmetic (F3.3).
 */
function VatGauge({ revenue }: { revenue: string }) {
  const level = vatWarningLevel(revenue);
  const value = Math.max(0, Number(revenue) || 0);
  const fraction = Math.min(value / VAT_STRONG_THRESHOLD, 1);

  const cx = 170;
  const cy = 160;
  const r = 140;
  const arcLen = Math.PI * r;
  const stroke =
    level === 'strong' ? '#dc2626' : level === 'soft' ? '#f59e0b' : '#2563eb';
  const pointAt = (f: number) => {
    const angle = Math.PI - f * Math.PI;
    return { x: cx + r * Math.cos(angle), y: cy - r * Math.sin(angle) };
  };
  const end = pointAt(fraction);
  const softTick = pointAt(VAT_SOFT_THRESHOLD / VAT_STRONG_THRESHOLD);

  const headroomCents = Math.max(
    0,
    VAT_STRONG_THRESHOLD * 100 - (moneyToCents(revenue) ?? 0)
  );

  return (
    <div className="grid grid-cols-1 items-center gap-8 rounded-2xl border border-hairline bg-white p-7 shadow-sm md:grid-cols-[340px_1fr]">
      {/* Arc */}
      <div className="relative mx-auto h-[190px] w-[340px]">
        <svg width="340" height="190" viewBox="0 0 340 190">
          <path
            d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
            fill="none"
            stroke="#eef2f7"
            strokeWidth="24"
            strokeLinecap="round"
          />
          <path
            d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
            fill="none"
            stroke={stroke}
            strokeWidth="24"
            strokeLinecap="round"
            strokeDasharray={`${fraction * arcLen} ${arcLen}`}
          />
          <line
            x1={softTick.x - 10}
            y1={softTick.y}
            x2={softTick.x + 4}
            y2={softTick.y - 12}
            stroke="#94a3b8"
            strokeWidth="2.5"
          />
          <circle
            cx={end.x}
            cy={end.y}
            r="8"
            fill={stroke}
            stroke="#fff"
            strokeWidth="4"
          />
        </svg>
        <div className="absolute inset-x-0 top-[92px] text-center">
          <div className="font-mono text-[30px] font-semibold tracking-tight text-gray-900">
            {formatMoney(revenue)}
          </div>
          <div className="mt-0.5 font-mono text-[11px] uppercase tracking-[0.12em] text-gray-400">
            Trailing 12 months
          </div>
        </div>
        <span className="absolute bottom-1 left-3 font-mono text-[11px] text-gray-300">
          $0
        </span>
        <span className="absolute bottom-1 right-2 font-mono text-[11px] text-gray-300">
          $100k
        </span>
      </div>

      {/* Explainer */}
      <div>
        {level !== 'none' && (
          <div
            data-testid="vat-warning-banner"
            data-level={level}
            role="alert"
            className={`mb-3.5 inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 ${
              level === 'strong'
                ? 'border-red-300 bg-red-50 text-red-800'
                : 'border-amber-300 bg-amber-50 text-amber-800'
            }`}
          >
            <span
              className={`h-2 w-2 rounded-full ${level === 'strong' ? 'bg-red-500' : 'bg-amber-500'}`}
            />
            <span className="text-xs font-semibold">
              {level === 'strong'
                ? 'VAT threshold exceeded · over $100,000'
                : 'Soft threshold crossed · over $80,000'}
            </span>
          </div>
        )}
        <h2 className="font-serif text-[24px] font-medium leading-snug text-gray-900">
          {level === 'none' ? (
            <>
              Comfortably below the{' '}
              <span className="italic">VAT registration ceiling.</span>
            </>
          ) : (
            <>
              Trailing-12-month revenue is{' '}
              <span className="italic">
                {Math.round(fraction * 100)}% of the ceiling.
              </span>
            </>
          )}
        </h2>
        <p className="mt-2.5 max-w-[52ch] text-sm leading-relaxed text-gray-600">
          {level === 'strong' ? (
            <>
              You are over the{' '}
              <strong className="text-gray-900">$100,000 BSD</strong>{' '}
              registration threshold. VAT registration is required — consult
              your accountant.
            </>
          ) : (
            <>
              You have{' '}
              <strong className="text-gray-900">
                {formatMoney(centsToMoney(headroomCents))}
              </strong>{' '}
              of headroom before the $100,000 BSD registration threshold.
              Crossing it makes VAT registration mandatory.
            </>
          )}
        </p>
        <div className="mt-5 flex gap-6">
          <GaugeStat
            label="Soft · $80k"
            value={value > VAT_SOFT_THRESHOLD ? 'Passed' : 'Below'}
            valueClass={
              value > VAT_SOFT_THRESHOLD ? 'text-green-600' : 'text-gray-500'
            }
          />
          <div className="w-px bg-gray-200" />
          <GaugeStat
            label="Headroom"
            value={formatMoney(centsToMoney(headroomCents))}
            valueClass="text-gray-900 font-mono"
          />
          <div className="w-px bg-gray-200" />
          <GaugeStat
            label="Ceiling · $100k"
            value={`${Math.max(0, Math.round((1 - fraction) * 100))}% away`}
            valueClass="text-red-600"
          />
        </div>
      </div>
    </div>
  );
}

function GaugeStat({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass: string;
}) {
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-[0.08em] text-gray-400">
        {label}
      </div>
      <div className={`mt-1 text-sm font-semibold ${valueClass}`}>{value}</div>
    </div>
  );
}

function StatCard({
  to,
  icon: Icon,
  cta,
  label,
  value,
  sub,
  testid,
}: {
  to: string;
  icon: typeof CheckCircleIcon;
  cta: string;
  label: string;
  value: string;
  sub: string;
  testid: string;
}) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group rounded-xl border border-hairline bg-white p-5 shadow-sm transition hover:border-primary-200 hover:shadow-md"
    >
      <div className="flex items-center justify-between">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50">
          <Icon className="h-5 w-5 text-primary-600" />
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600">
          {cta}
          <ArrowRightIcon className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
        </span>
      </div>
      <p className="mt-4 text-[13px] font-medium text-gray-500">{label}</p>
      <p className="mt-1 font-mono text-[32px] font-semibold text-gray-900">
        {value}
      </p>
      <p className="mt-1 text-xs text-gray-400">{sub}</p>
    </Link>
  );
}

/** Trailing-12-month invoice revenue, derived client-side from the documents
 * list (F8.2 basis: invoices, ISSUED|PAID, excl. VOID). NOT a server metric —
 * labelled as such; the full breakdown lives on the Revenue view. */
function useDerivedMonthlyRevenue() {
  const { data } = useQuery({
    queryKey: ['documents', 'dashboard-spark'],
    queryFn: () => documentsApi.list({ page: 1 }),
    staleTime: 60_000,
  });

  return useMemo(() => {
    const today = nassauToday();
    const months: { key: string; label: string }[] = [];
    const [y, m] = today.split('-').map(Number);
    for (let i = 11; i >= 0; i--) {
      const d = new Date(Date.UTC(y!, m! - 1 - i, 1));
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      months.push({
        key,
        label: d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' }),
      });
    }
    const buckets = new Map(months.map(mo => [mo.key, 0]));
    const items: DocumentListItem[] = data?.items ?? [];
    for (const doc of items) {
      if (doc.type !== 'invoice') continue;
      if (doc.status !== 'ISSUED' && doc.status !== 'PAID') continue;
      if (!doc.issue_date) continue;
      const key = doc.issue_date.slice(0, 7);
      if (!buckets.has(key)) continue;
      const cents = moneyToCents(doc.total);
      if (cents !== null) buckets.set(key, buckets.get(key)! + cents);
    }
    const series = months.map(mo => ({
      ...mo,
      cents: buckets.get(mo.key) ?? 0,
    }));
    const max = Math.max(1, ...series.map(s => s.cents));
    return { series, max, hasData: items.length > 0 };
  }, [data]);
}

function RevenueSparkline() {
  const { series, max, hasData } = useDerivedMonthlyRevenue();
  const w = 1240;
  const h = 96;
  const points = series.map((s, i) => {
    const x = (i / (series.length - 1)) * w;
    const yv = h - (s.cents / max) * (h - 10) - 2;
    return { x, y: yv };
  });
  const line = points.map(p => `${p.x} ${p.y}`).join(' L ');
  const area = `M ${line} L ${w} ${h} L 0 ${h} Z`;
  const last = points[points.length - 1]!;

  return (
    <div className="rounded-xl border border-hairline bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-[15px] font-semibold text-gray-900">
            Revenue, last 12 months
          </h3>
          <p className="mt-0.5 font-mono text-[11px] text-gray-400">
            Derived client-side from documents · not a server metric
          </p>
        </div>
        <Link
          to="/revenue"
          className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600"
        >
          Open Revenue view
          <ArrowRightIcon className="h-3.5 w-3.5" />
        </Link>
      </div>
      {hasData ? (
        <>
          <svg
            width="100%"
            height={h}
            viewBox={`0 0 ${w} ${h}`}
            preserveAspectRatio="none"
            aria-hidden
          >
            <defs>
              <linearGradient id="spark" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#3b82f6" stopOpacity="0.22" />
                <stop offset="1" stopColor="#3b82f6" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={area} fill="url(#spark)" />
            <path
              d={`M ${line}`}
              fill="none"
              stroke="#2563eb"
              strokeWidth="2.5"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
            <circle cx={last.x} cy={last.y} r="4.5" fill="#2563eb" />
          </svg>
          <div className="mt-2 flex justify-between">
            {series
              .filter((_, i) => i % 2 === 0)
              .map(s => (
                <span
                  key={s.key}
                  className="font-mono text-[10px] text-gray-300"
                >
                  {s.label}
                </span>
              ))}
          </div>
        </>
      ) : (
        <p className="py-6 text-center text-sm text-gray-400">
          No issued invoices yet to chart.
        </p>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data, isLoading, isError, refetch } = useQuery<DashboardData>({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.get(),
  });

  if (isLoading) {
    return (
      <div
        data-testid="dashboard-loading"
        className="flex justify-center py-20"
      >
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div data-testid="dashboard-error">
        <EmptyState
          title="Couldn't load the dashboard"
          description="Something went wrong fetching your summary. Please try again."
          action={{ label: 'Retry', onClick: () => refetch() }}
        />
      </div>
    );
  }

  const firstName = user?.name?.trim().split(/\s+/)[0];

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div>
        <h1 className="font-serif text-[32px] font-medium tracking-tight text-gray-900">
          {greeting()}
          {firstName ? (
            <>
              , <span className="italic text-primary-800">{firstName}</span>.
            </>
          ) : (
            '.'
          )}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Here's where the studio stands this month.
        </p>
      </div>

      <VatGauge revenue={data.trailing_12m_revenue} />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <StatCard
          testid="stat-issued-this-month"
          to="/documents"
          icon={CheckCircleIcon}
          cta="This month"
          label="Issued this month"
          value={String(data.issued_this_month)}
          sub="Documents dated this month"
        />
        <StatCard
          testid="stat-outstanding-total"
          to="/documents?type=invoice&status=ISSUED"
          icon={BanknotesIcon}
          cta="ISSUED invoices"
          label="Outstanding"
          value={formatMoney(data.outstanding_total)}
          sub="Awaiting payment"
        />
        <StatCard
          testid="stat-trailing-12m-revenue"
          to="/revenue"
          icon={ArrowTrendingUpIcon}
          cta="Revenue view"
          label="Trailing 12-month revenue"
          value={formatMoney(data.trailing_12m_revenue)}
          sub="Rolling window · Nassau time"
        />
      </div>

      <RevenueSparkline />
    </div>
  );
}
